import React from 'react';
import { Button } from 'react-bootstrap';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { useQuizSoundPreferenceOptional } from '../contexts/QuizSoundPreferenceContext';
import { getQuizSfxEnabled, setQuizSfxEnabledLocal } from '../audio/quizSfx';

type QuizSoundToggleButtonProps = {
  /** Disable during save overlay / blocking UI */
  disabled?: boolean;
};

/**
 * Left of the quit button: toggles quiz feedback SFX. Preference syncs with server when logged in.
 */
const QuizSoundToggleButton: React.FC<QuizSoundToggleButtonProps> = ({ disabled = false }) => {
  const { t } = useSafeTranslation();
  const ctx = useQuizSoundPreferenceOptional();
  const soundsEnabled = ctx?.soundsEnabled ?? getQuizSfxEnabled();
  const busy = ctx?.busy ?? false;

  const handleClick = () => {
    const next = !soundsEnabled;
    if (ctx) {
      void ctx.setSoundsEnabled(next);
    } else {
      setQuizSfxEnabledLocal(next);
    }
  };

  const muted = !soundsEnabled;

  return (
    <Button
      type="button"
      variant="outline-secondary"
      onClick={handleClick}
      disabled={disabled || busy}
      className="quiz-sound-toggle-btn flex-shrink-0"
      aria-pressed={muted}
      title={
        muted
          ? t('quiz.soundMutedToggle', 'Sound off — click to enable quiz sounds')
          : t('quiz.soundOnToggle', 'Sound on — click to mute quiz sounds')
      }
    >
      <i className={`bi ${muted ? 'bi-volume-mute-fill' : 'bi-volume-up-fill'}`} aria-hidden />
      <span className="visually-hidden">
        {muted ? t('quiz.soundsMuted', 'Quiz sounds muted') : t('quiz.soundsOn', 'Quiz sounds on')}
      </span>
    </Button>
  );
};

export default QuizSoundToggleButton;
